/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderFormSchemaFactory,
  BuilderFormStateSchema,
  BuilderUserContext, FormRootSchema,
  FormState, FormStateSchema,
  updateChildrenSchema,
  whenCreateOrEditMode,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  MilestoneEventFiresType,
  MilestoneEventFromToType,
  MilestoneEventType,
  MilestoneSendMethodType,
  UserSystemRole,
} from '@gh/builder-shared-data';
import { isRouteForCreationMode, NumberTypes, toAppFormGroup } from '@gh/common-shared';
import { FormMode, getControlValue, setControlValue } from '@gh/core-ui';

import { get, includes } from 'lodash';

import {
  CostCenterAutocompleteListItem,
  EmailTemplateAutocompleteListItem,
  MilestoneEventDetails,
} from '../../resources/milestone-event-details.model';

@Injectable()
export class MilestoneEventDetailsFormState extends FormState<MilestoneEventDetails> {

  constructor(private userContext: BuilderUserContext,
              private route: ActivatedRoute,
              schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);
  }

  clearCostCenter(): void {
    this.group.markAsDirty();
    setControlValue(this.group, 'costCenter', void 0);
  }

  costCenterValue(): CostCenterAutocompleteListItem {
    return getControlValue(this.group, 'costCenter');
  }

  setCostCenterValue(costCenterItem: CostCenterAutocompleteListItem): void {
    this.group.markAsDirty();
    setControlValue(this.group, 'costCenter', costCenterItem);
  }

  updateCostCenterSchema(isCostCenterEventDate: boolean): void {
    this.get('costCenter').updateSchema({
      required: isCostCenterEventDate,
      visible: isCostCenterEventDate,
      enabled: isCostCenterEventDate,
    });
  }

  updateSendToSystemRoleSchema(isGroupSystemRoleList: boolean): void {
    this.get('sendToSystemRole').updateSchema({
      required: isGroupSystemRoleList,
      visible: isGroupSystemRoleList,
    });
  }

  updateTemplate(template: EmailTemplateAutocompleteListItem): void {
    setControlValue(this.group, 'template', template);
  }

  updateSendMethod(sendMethod: MilestoneSendMethodType): void {
    setControlValue(this.group, 'sendMethod', sendMethod);
    this.clearDependentFields(sendMethod);

    updateChildrenSchema(toAppFormGroup(this.group), {
      sendFrom: { visible: sendMethod === MilestoneSendMethodType.Email },
      sendFromSystemRole: { visible: sendMethod === MilestoneSendMethodType.Email },
    });
  }

  updateNumberOfDaysSchema(isBeforeOrAfter: boolean): void {
    this.get('numberOfDays').updateSchema({
      visible: isBeforeOrAfter,
      required: isBeforeOrAfter,
    });
  }

  updateSendFromSystemRole(sendFromSystemRoleValue: Maybe<UserSystemRole>): void {
    setControlValue(this.group, 'sendFromSystemRole', sendFromSystemRoleValue);
  }

  protected buildSchema(milestoneEvent: MilestoneEventDetails, { root }: FormStateSchema): FormRootSchema {
    let mode: FormMode;
    if (isRouteForCreationMode(this.route)) {
      mode = FormMode.CreateMode;
    } else if (this.userContext.hasPermission(BuilderPermission.Setup.MilestoneEvents.EDIT)) {
      mode = FormMode.EditMode;
    } else {
      mode = FormMode.ViewMode;
    }

    return root({
      mode,
      enabled: whenCreateOrEditMode(),
      optimisticLock: true,
      children: {
        milestoneEventId: void 0,
        eventDate: { required: true },
        costCenter: {
          visible: milestoneEvent.eventDate
            && (MilestoneEventType.PriceBookWithdrawalDate === milestoneEvent.eventDate.id
              || MilestoneEventType.SalesFollowUpDate === milestoneEvent.eventDate.id),
          required: milestoneEvent.eventDate && includes(
            [MilestoneEventType.SupervisorScheduleActivityEndDate, MilestoneEventType.SupervisorScheduleActivityStartDate],
            milestoneEvent.eventDate.id),
        },
        sendMethod: void 0,
        template: { required: true },
        sendWhen: void 0,
        numberOfDays: {
          required: milestoneEvent.sendWhen === MilestoneEventFiresType.After
            || milestoneEvent.sendWhen === MilestoneEventFiresType.Before,
          visible: milestoneEvent.sendWhen === MilestoneEventFiresType.After
            || milestoneEvent.sendWhen === MilestoneEventFiresType.Before,
          type: NumberTypes.Days,
        },
        sendFrom: { visible: milestoneEvent.sendMethod === MilestoneSendMethodType.Email, required: true },
        sendFromSystemRole: { visible: milestoneEvent.sendMethod === MilestoneSendMethodType.Email, required: true },
        sendTo: { required: true },
        sendToSystemRole: {
          required: milestoneEvent.sendTo === MilestoneEventFromToType.IndSystemRole
            || milestoneEvent.sendTo === MilestoneEventFromToType.GroupSystemRole,
          visible: milestoneEvent.sendTo === MilestoneEventFromToType.IndSystemRole
            || milestoneEvent.sendTo === MilestoneEventFromToType.GroupSystemRole,
        },
        version: void 0,
      },
    });
  }

  private clearDependentFields(sendMethod: MilestoneSendMethodType): void {
    setControlValue(this.group, 'template', void 0);
    if (sendMethod === MilestoneSendMethodType.Sms) {
      setControlValue(this.group, 'sendFrom', void 0);
      setControlValue(this.group, 'sendFromSystemRole', void 0);
    }
  }

  get isCostCenterControlRequired(): boolean {
    return this.isSupervisorDateTrigger;
  }

  get isSupervisorDateTrigger(): boolean {
    const supervisorDateTriggerList: MilestoneEventType[] =
      [MilestoneEventType.SupervisorScheduleActivityEndDate, MilestoneEventType.SupervisorScheduleActivityStartDate];
    return includes(supervisorDateTriggerList, get(getControlValue(this.group, 'eventDate'), 'id', void 0));
  }

  get isNumberOfDaysVisible(): boolean {
    return includes(
      [MilestoneEventFiresType.After, MilestoneEventFiresType.Before],
      getControlValue(this.group, 'sendWhen'));
  }
}
